/**
 * Dashboard Administrativo — redesign moderno
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Grid, Typography, Card, CardContent, CircularProgress,
  Alert, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, InputAdornment, Select, MenuItem, FormControl,
  Chip, Avatar, IconButton, Tooltip, Pagination, List, ListItem,
  ListItemAvatar, ListItemText, Divider, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Paper,
} from '@mui/material';
import {
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  AttachMoney as AttachMoneyIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  Search as SearchIcon,
  Visibility as VisibilityIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Analytics as AnalyticsIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as ReTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import adminService from '../../services/adminService';

/* ─── palette Gerêncie ─── */
const ORANGE   = '#F97316';
const ORANGE_L = '#FB923C';
const BLUE     = '#2563EB';
const AMBER    = '#F59E0B';
const GREEN    = '#10B981';
const RED      = '#EF4444';
const PIE_COLORS = ['#F97316', '#2563EB', '#10B981'];

/* ─── helper: formatar moeda ─── */
const fmt = (v) =>
  `R$ ${parseFloat(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ─── helper: formatar data ─── */
const fmtDate = (d) =>
  new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });

/* ─── KPI Card ─── */
const KpiCard = ({ title, value, subtitle, icon: Icon, gradient, delta }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: 3,
      border: '1px solid #E2E8F0',
      bgcolor: '#fff',
      overflow: 'hidden',
      position: 'relative',
      transition: 'transform 0.18s, box-shadow 0.18s',
      '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 8px 24px rgba(0,0,0,0.09)' },
    }}
  >
    {/* top accent bar */}
    <Box sx={{ height: 4, background: gradient }} />
    <CardContent sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#94A3B8', letterSpacing: '0.05em', textTransform: 'uppercase', mb: 0.5 }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 26, fontWeight: 800, color: '#0F172A', lineHeight: 1.2 }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: 12, color: '#64748B', mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
          {delta !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.8 }}>
              {delta >= 0
                ? <ArrowUpIcon sx={{ fontSize: 14, color: GREEN }} />
                : <ArrowDownIcon sx={{ fontSize: 14, color: RED }} />}
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: delta >= 0 ? GREEN : RED }}>
                {Math.abs(delta)}% vs mês anterior
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            width: 48, height: 48,
            borderRadius: 2,
            background: gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            flexShrink: 0,
          }}
        >
          <Icon sx={{ color: '#fff', fontSize: 24 }} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

/* ─── section header ─── */
const SectionHeader = ({ icon: Icon, title, subtitle }) => (
  <Box sx={{ mb: 2.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Icon sx={{ fontSize: 20, color: ORANGE }} />
      <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#0F172A' }}>{title}</Typography>
    </Box>
    {subtitle && (
      <Typography sx={{ fontSize: 12, color: '#94A3B8', mt: 0.3 }}>{subtitle}</Typography>
    )}
  </Box>
);

/* ─── tipo chip ─── */
const TipoChip = ({ tipo }) => {
  const map = {
    PF:      { label: 'PF',      bg: 'rgba(249,115,22,0.1)',  color: ORANGE },
    PJ:      { label: 'PJ',      bg: 'rgba(14,165,233,0.1)',  color: BLUE   },
    HIBRIDO: { label: 'Híbrido', bg: 'rgba(245,158,11,0.1)', color: AMBER  },
  };
  const s = map[tipo] || { label: tipo, bg: '#F1F5F9', color: '#64748B' };
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{ bgcolor: s.bg, color: s.color, fontWeight: 700, fontSize: 11, height: 22, border: 'none' }}
    />
  );
};

/* ═══════════════════════════════════════════════════════════ */
const DashboardAdmin = () => {
  const [loading,           setLoading]          = useState(true);
  const [error,             setError]            = useState('');
  const [estatisticas,      setEstatisticas]     = useState(null);
  const [usuarios,          setUsuarios]         = useState([]);
  const [page,              setPage]             = useState(1);
  const [totalPaginas,      setTotalPaginas]     = useState(1);
  const [search,            setSearch]           = useState('');
  const [ordenar,           setOrdenar]          = useState('createdAt');
  const [ordem,             setOrdem]            = useState('DESC');
  const [atividades,        setAtividades]       = useState([]);
  const [graficoCrescimento,setGraficoCrescimento] = useState([]);
  const [distribuicaoTipos, setDistribuicaoTipos] = useState([]);
  const [modalOpen,         setModalOpen]        = useState(false);
  const [usuarioSel,        setUsuarioSel]       = useState(null);
  const [detalhes,          setDetalhes]         = useState(null);
  const [loadingDet,        setLoadingDet]       = useState(false);

  /* ── load all ── */
  const carregarDados = useCallback(async () => {
    try {
      setLoading(true); setError('');
      const [est, usr, atv, cres, dist] = await Promise.all([
        adminService.getEstatisticasGerais(),
        adminService.listarUsuarios({ page, search, ordenar, ordem }),
        adminService.getAtividadesRecentes(20),
        adminService.getGraficoCrescimento(12),
        adminService.getDistribuicaoTipos(),
      ]);
      setEstatisticas(est);
      setUsuarios(usr.usuarios);
      setTotalPaginas(usr.totalPaginas);
      setAtividades(atv);
      setGraficoCrescimento(cres);
      setDistribuicaoTipos(dist);
    } catch (e) {
      setError('Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  const carregarUsuarios = useCallback(async () => {
    try {
      const d = await adminService.listarUsuarios({ page, search, ordenar, ordem });
      setUsuarios(d.usuarios);
      setTotalPaginas(d.totalPaginas);
    } catch (e) { /* silencioso */ }
  }, [page, search, ordenar, ordem]);

  useEffect(() => { carregarDados(); }, []); // eslint-disable-line
  useEffect(() => { if (!loading) carregarUsuarios(); }, [page, search, ordenar, ordem]); // eslint-disable-line

  const verDetalhes = async (u) => {
    setUsuarioSel(u); setModalOpen(true); setLoadingDet(true);
    try {
      const d = await adminService.getUsuarioDetalhes(u.id);
      setDetalhes(d);
    } finally { setLoadingDet(false); }
  };

  const toggleStatus = async (id) => {
    await adminService.toggleStatusUsuario(id);
    carregarUsuarios();
    if (detalhes?.usuario?.id === id) setModalOpen(false);
  };

  /* ── pie data ── */
  const pieData = distribuicaoTipos.map((item) => ({
    name:  item.tipo === 'PF' ? 'Pessoa Física' : item.tipo === 'PJ' ? 'Pessoa Jurídica' : 'Híbrido',
    value: item.total,
  }));

  /* ── custom pie label ── */
  const renderPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const r = innerRadius + (outerRadius - innerRadius) * 0.55;
    const x = cx + r * Math.cos(-midAngle * RADIAN);
    const y = cy + r * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={700}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: ORANGE }} size={48} thickness={3} />
          <Typography sx={{ mt: 2, color: '#64748B', fontSize: 14 }}>Carregando painel...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: 4 }}>
      {/* ── Header ── */}
      <Box
        sx={{
          mb: 3,
          p: 3,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #0F172A 0%, #1E3A5F 60%, #0C2340 100%)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* decorative circles */}
        <Box sx={{
          position: 'absolute', right: -40, top: -40,
          width: 200, height: 200, borderRadius: '50%',
          bgcolor: 'rgba(167,139,250,0.08)',
        }} />
        <Box sx={{
          position: 'absolute', right: 80, bottom: -60,
          width: 140, height: 140, borderRadius: '50%',
          bgcolor: 'rgba(167,139,250,0.05)',
        }} />

        <Box sx={{ position: 'relative' }}>
          <Typography sx={{ fontSize: 22, fontWeight: 800, color: '#fff', mb: 0.5 }}>
            Painel Administrativo
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'rgba(147,197,253,0.85)' }}>
            Visão geral do sistema · {new Date().toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
          </Typography>
          {estatisticas && (
            <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
              {[
                { label: 'Usuários ativos', val: estatisticas.usuariosAtivos },
                { label: 'Novas clínicas', val: estatisticas.novosUsuariosMes + ' este mês' },
                { label: 'Lançamentos totais', val: estatisticas.totalLancamentos },
              ].map((s) => (
                <Box
                  key={s.label}
                  sx={{
                    px: 2, py: 0.8,
                    bgcolor: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(4px)',
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.15)',
                  }}
                >
                  <Typography sx={{ fontSize: 11, color: 'rgba(147,197,253,0.7)', mb: 0.2 }}>{s.label}</Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{s.val}</Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* ── KPI Cards ── */}
      {estatisticas && (
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} lg={3}>
            <KpiCard
              title="Total de Usuários"
              value={estatisticas.totalUsuarios}
              subtitle={`${estatisticas.usuariosAtivos} ativos no sistema`}
              icon={PeopleIcon}
              gradient={`linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_L} 100%)`}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <KpiCard
              title="Novos este Mês"
              value={estatisticas.novosUsuariosMes}
              subtitle="Cadastros no período"
              icon={PersonAddIcon}
              gradient={`linear-gradient(135deg, ${BLUE} 0%, #38BDF8 100%)`}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <KpiCard
              title="Lançamentos"
              value={estatisticas.totalLancamentos}
              subtitle={`${estatisticas.totalAnalises} análises realizadas`}
              icon={AssignmentIcon}
              gradient={`linear-gradient(135deg, ${AMBER} 0%, #FBBF24 100%)`}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <KpiCard
              title="Saldo Consolidado"
              value={fmt(estatisticas.saldoTotal)}
              subtitle={`Fat: ${fmt(estatisticas.faturamentoTotal)}`}
              icon={AttachMoneyIcon}
              gradient={
                estatisticas.saldoTotal >= 0
                  ? `linear-gradient(135deg, ${GREEN} 0%, #34D399 100%)`
                  : `linear-gradient(135deg, ${RED} 0%, #F87171 100%)`
              }
            />
          </Grid>
        </Grid>
      )}

      {/* ── Charts Row ── */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {/* Area chart */}
        <Grid item xs={12} md={8}>
          <Card
            elevation={0}
            sx={{ borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#fff', height: '100%' }}
          >
            <CardContent sx={{ p: 3 }}>
              <SectionHeader
                icon={TrendingUpIcon}
                title="Crescimento de Usuários"
                subtitle="Novos cadastros por mês nos últimos 12 meses"
              />
              {graficoCrescimento.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <AreaChart data={graficoCrescimento} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor={ORANGE} stopOpacity={0.25} />
                        <stop offset="95%" stopColor={ORANGE} stopOpacity={0.0}  />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                    <XAxis
                      dataKey="mes"
                      tick={{ fontSize: 11, fill: '#94A3B8' }}
                      axisLine={false} tickLine={false}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#94A3B8' }}
                      axisLine={false} tickLine={false}
                      allowDecimals={false}
                    />
                    <ReTooltip
                      contentStyle={{
                        borderRadius: 10, border: '1px solid #E2E8F0',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                        fontSize: 12,
                      }}
                      formatter={(v) => [v, 'Usuários']}
                    />
                    <Area
                      type="monotone"
                      dataKey="total"
                      stroke={ORANGE}
                      strokeWidth={2.5}
                      fill="url(#areaGrad)"
                      dot={{ fill: ORANGE, r: 4, strokeWidth: 0 }}
                      activeDot={{ r: 6, fill: ORANGE_L }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: '#CBD5E1', fontSize: 13 }}>Dados insuficientes</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Pie chart */}
        <Grid item xs={12} md={4}>
          <Card
            elevation={0}
            sx={{ borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#fff', height: '100%' }}
          >
            <CardContent sx={{ p: 3 }}>
              <SectionHeader
                icon={AnalyticsIcon}
                title="Distribuição por Tipo"
                subtitle="Perfil dos usuários cadastrados"
              />
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="45%"
                      outerRadius={90}
                      dataKey="value"
                      labelLine={false}
                      label={renderPieLabel}
                    >
                      {pieData.map((_, i) => (
                        <Cell
                          key={i}
                          fill={PIE_COLORS[i % PIE_COLORS.length]}
                          stroke="none"
                        />
                      ))}
                    </Pie>
                    <Legend
                      iconType="circle"
                      iconSize={8}
                      wrapperStyle={{ fontSize: 12, color: '#64748B', paddingTop: 8 }}
                    />
                    <ReTooltip
                      formatter={(v, name) => [v + ' usuários', name]}
                      contentStyle={{
                        borderRadius: 10, border: '1px solid #E2E8F0',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.08)', fontSize: 12,
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: '#CBD5E1', fontSize: 13 }}>Sem dados</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Table + Activities ── */}
      <Grid container spacing={2.5}>
        {/* Tabela de usuários */}
        <Grid item xs={12} lg={8}>
          <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#fff' }}>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader
                icon={PeopleIcon}
                title="Usuários da Plataforma"
                subtitle="Gerencie todos os usuários cadastrados"
              />

              {/* Filtros */}
              <Box sx={{ display: 'flex', gap: 1.5, mb: 2.5, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder="Buscar nome, email ou clínica..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: '#94A3B8' }} />
                      </InputAdornment>
                    ),
                    sx: { borderRadius: 2, fontSize: 13 },
                  }}
                  sx={{ flexGrow: 1, minWidth: 200 }}
                />
                <Select
                  size="small"
                  value={ordenar}
                  onChange={(e) => setOrdenar(e.target.value)}
                  sx={{ minWidth: 130, borderRadius: 2, fontSize: 13 }}
                >
                  <MenuItem value="createdAt" sx={{ fontSize: 13 }}>Cadastro</MenuItem>
                  <MenuItem value="nome"      sx={{ fontSize: 13 }}>Nome</MenuItem>
                  <MenuItem value="email"     sx={{ fontSize: 13 }}>Email</MenuItem>
                </Select>
                <Select
                  size="small"
                  value={ordem}
                  onChange={(e) => setOrdem(e.target.value)}
                  sx={{ minWidth: 90, borderRadius: 2, fontSize: 13 }}
                >
                  <MenuItem value="DESC" sx={{ fontSize: 13 }}>Desc</MenuItem>
                  <MenuItem value="ASC"  sx={{ fontSize: 13 }}>Asc</MenuItem>
                </Select>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {['Usuário', 'Tipo', 'Lançamentos', 'Saldo', 'Status', 'Cadastro', ''].map((h) => (
                        <TableCell
                          key={h}
                          align={['Lançamentos', 'Saldo'].includes(h) ? 'right' : 'left'}
                          sx={{
                            fontSize: 10, fontWeight: 700, color: '#94A3B8',
                            letterSpacing: '0.07em', textTransform: 'uppercase',
                            borderBottom: '2px solid #F1F5F9', pb: 1.2,
                          }}
                        >
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usuarios.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                          <Typography sx={{ color: '#CBD5E1', fontSize: 13 }}>
                            Nenhum usuário encontrado
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      usuarios.map((u) => {
                        const saldo = (u.valorTotalFaturamento || 0) - (u.valorTotalDespesas || 0);
                        return (
                          <TableRow
                            key={u.id}
                            sx={{
                              '&:hover': { bgcolor: 'rgba(249,115,22,0.03)' },
                              borderBottom: '1px solid #F8FAFC',
                            }}
                          >
                            {/* Usuário */}
                            <TableCell sx={{ py: 1.5, borderBottom: 'none' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Avatar
                                  sx={{
                                    width: 32, height: 32, fontSize: 13, fontWeight: 700,
                                    background: `linear-gradient(135deg, ${ORANGE} 0%, ${ORANGE_L} 100%)`,
                                  }}
                                >
                                  {u.nome?.charAt(0).toUpperCase() || 'U'}
                                </Avatar>
                                <Box>
                                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#1E293B', lineHeight: 1.2 }}>
                                    {u.nome}
                                  </Typography>
                                  <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>
                                    {u.email}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>

                            {/* Tipo */}
                            <TableCell sx={{ borderBottom: 'none' }}>
                              <TipoChip tipo={u.tipoPessoa} />
                            </TableCell>

                            {/* Lançamentos */}
                            <TableCell align="right" sx={{ borderBottom: 'none' }}>
                              <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>
                                {(u.totalFaturamentos || 0) + (u.totalDespesas || 0)}
                              </Typography>
                            </TableCell>

                            {/* Saldo */}
                            <TableCell align="right" sx={{ borderBottom: 'none' }}>
                              <Typography sx={{ fontSize: 13, fontWeight: 700, color: saldo >= 0 ? GREEN : RED }}>
                                {fmt(saldo)}
                              </Typography>
                            </TableCell>

                            {/* Status */}
                            <TableCell sx={{ borderBottom: 'none' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                                <Box
                                  sx={{
                                    width: 7, height: 7, borderRadius: '50%',
                                    bgcolor: u.ativo ? GREEN : '#CBD5E1',
                                  }}
                                />
                                <Typography sx={{ fontSize: 12, color: u.ativo ? GREEN : '#94A3B8', fontWeight: 600 }}>
                                  {u.ativo ? 'Ativo' : 'Inativo'}
                                </Typography>
                              </Box>
                            </TableCell>

                            {/* Cadastro */}
                            <TableCell sx={{ borderBottom: 'none' }}>
                              <Typography sx={{ fontSize: 11, color: '#94A3B8' }}>
                                {fmtDate(u.createdAt)}
                              </Typography>
                            </TableCell>

                            {/* Ações */}
                            <TableCell align="right" sx={{ borderBottom: 'none' }}>
                              <Tooltip title="Ver detalhes">
                                <IconButton
                                  size="small"
                                  onClick={() => verDetalhes(u)}
                                  sx={{ color: ORANGE, '&:hover': { bgcolor: 'rgba(249,115,22,0.08)' } }}
                                >
                                  <VisibilityIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={u.ativo ? 'Desativar' : 'Ativar'}>
                                <IconButton
                                  size="small"
                                  onClick={() => toggleStatus(u.id)}
                                  sx={{
                                    color: u.ativo ? RED : GREEN,
                                    '&:hover': { bgcolor: u.ativo ? 'rgba(239,68,68,0.08)' : 'rgba(16,185,129,0.08)' },
                                  }}
                                >
                                  {u.ativo
                                    ? <BlockIcon sx={{ fontSize: 17 }} />
                                    : <CheckCircleIcon sx={{ fontSize: 17 }} />}
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={totalPaginas}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  size="small"
                  sx={{
                    '& .MuiPaginationItem-root': { fontSize: 12 },
                    '& .Mui-selected': { bgcolor: `${ORANGE} !important`, color: '#fff' },
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Atividades recentes */}
        <Grid item xs={12} lg={4}>
          <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#fff', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <SectionHeader
                icon={TimeIcon}
                title="Atividades Recentes"
                subtitle="Últimas ações no sistema"
              />
              <Box sx={{ maxHeight: 480, overflowY: 'auto', pr: 0.5 }}>
                {atividades.length === 0 ? (
                  <Typography sx={{ color: '#CBD5E1', fontSize: 13, textAlign: 'center', py: 4 }}>
                    Nenhuma atividade recente
                  </Typography>
                ) : (
                  atividades.map((a, i) => (
                    <Box
                      key={i}
                      sx={{
                        display: 'flex', gap: 1.5, mb: 2,
                        pb: 2,
                        borderBottom: i < atividades.length - 1 ? '1px solid #F1F5F9' : 'none',
                      }}
                    >
                      {/* dot line */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.5 }}>
                        <Box
                          sx={{
                            width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            background: a.tipo === 'novo_usuario'
                              ? `linear-gradient(135deg, ${ORANGE}, ${ORANGE_L})`
                              : `linear-gradient(135deg, ${BLUE}, #38BDF8)`,
                          }}
                        >
                          {a.tipo === 'novo_usuario'
                            ? <PersonAddIcon sx={{ fontSize: 14, color: '#fff' }} />
                            : <AnalyticsIcon sx={{ fontSize: 14, color: '#fff' }} />}
                        </Box>
                        {i < atividades.length - 1 && (
                          <Box sx={{ width: 1, flex: 1, bgcolor: '#F1F5F9', mt: 0.5 }} />
                        )}
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#1E293B', lineHeight: 1.3 }}>
                          {a.descricao}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: '#94A3B8', mt: 0.3 }}>
                          {new Date(a.data).toLocaleString('pt-BR', {
                            day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                          })}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Modal Detalhes ── */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, border: '1px solid #E2E8F0' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {usuarioSel && (
              <Avatar sx={{ background: `linear-gradient(135deg, ${ORANGE}, ${ORANGE_L})`, width: 38, height: 38 }}>
                {usuarioSel.nome?.charAt(0).toUpperCase()}
              </Avatar>
            )}
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 16, color: '#0F172A' }}>
                {usuarioSel?.nome}
              </Typography>
              <Typography sx={{ fontSize: 12, color: '#94A3B8' }}>{usuarioSel?.email}</Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          {loadingDet ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress sx={{ color: ORANGE }} size={36} />
            </Box>
          ) : detalhes ? (
            <Grid container spacing={2}>
              {/* info */}
              <Grid item xs={12} sm={6}>
                {[
                  ['Tipo de Pessoa', <TipoChip tipo={detalhes.usuario.tipoPessoa} />],
                  ['Cadastrado em',  fmtDate(detalhes.usuario.createdAt)],
                  ['Status',
                    <Chip
                      label={detalhes.usuario.ativo ? 'Ativo' : 'Inativo'}
                      size="small"
                      sx={{
                        bgcolor: detalhes.usuario.ativo ? 'rgba(16,185,129,0.1)' : '#F1F5F9',
                        color: detalhes.usuario.ativo ? GREEN : '#94A3B8',
                        fontWeight: 700, fontSize: 11,
                      }}
                    />
                  ],
                ].map(([label, value]) => (
                  <Box key={label} sx={{ mb: 2 }}>
                    <Typography sx={{ fontSize: 11, color: '#94A3B8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.4 }}>
                      {label}
                    </Typography>
                    {typeof value === 'string'
                      ? <Typography sx={{ fontSize: 14, color: '#1E293B', fontWeight: 500 }}>{value}</Typography>
                      : value}
                  </Box>
                ))}
              </Grid>

              {/* stats */}
              <Grid item xs={12} sm={6}>
                <Paper
                  elevation={0}
                  sx={{ p: 2, bgcolor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#64748B', mb: 1.5, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Estatísticas
                  </Typography>
                  {[
                    ['Faturamentos',  detalhes.estatisticas.totalFaturamentos, ORANGE],
                    ['Despesas',      detalhes.estatisticas.totalDespesas,      AMBER],
                    ['Análises',      detalhes.estatisticas.totalAnalises,      BLUE],
                  ].map(([l, v, c]) => (
                    <Box key={l} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography sx={{ fontSize: 13, color: '#64748B' }}>{l}</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: c }}>{v}</Typography>
                    </Box>
                  ))}
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                    <Typography sx={{ fontSize: 13, color: '#64748B' }}>Faturamento total</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: GREEN }}>
                      {fmt(detalhes.estatisticas.valorTotalFaturamento)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.8 }}>
                    <Typography sx={{ fontSize: 13, color: '#64748B' }}>Despesas total</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: RED }}>
                      {fmt(detalhes.estatisticas.valorTotalDespesas)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex', justifyContent: 'space-between',
                      pt: 1, mt: 1, borderTop: '1px dashed #E2E8F0',
                    }}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1E293B' }}>Saldo</Typography>
                    <Typography
                      sx={{
                        fontSize: 14, fontWeight: 800,
                        color: detalhes.estatisticas.saldo >= 0 ? GREEN : RED,
                      }}
                    >
                      {fmt(detalhes.estatisticas.saldo)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          ) : (
            <Typography sx={{ color: '#94A3B8', textAlign: 'center', py: 3 }}>
              Erro ao carregar detalhes
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => setModalOpen(false)}
            variant="outlined"
            size="small"
            sx={{ borderRadius: 2, borderColor: '#E2E8F0', color: '#64748B', fontSize: 13 }}
          >
            Fechar
          </Button>
          {usuarioSel && (
            <Button
              onClick={() => toggleStatus(usuarioSel.id)}
              variant="contained"
              size="small"
              sx={{
                borderRadius: 2, fontSize: 13,
                bgcolor: usuarioSel.ativo ? RED : GREEN,
                '&:hover': { bgcolor: usuarioSel.ativo ? '#DC2626' : '#059669' },
              }}
            >
              {usuarioSel.ativo ? 'Desativar usuário' : 'Ativar usuário'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardAdmin;
